/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../services/user.service';
import { XrxControlBaseComponent } from '../controls/xrx-control-base.component';
import { SecurityLevelHelpComponent } from './modals/security-level-help.component';
import {
  AddressType,
  EditAddressComponent,
} from '../modals/edit-address.component';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { XdaStringsService } from '../../services/xda-strings.service';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { Constants, Language } from '../../../common/constants';
import { EmailService } from '../../services/email.service';
import { AccessListService } from '../../services/access-list.service';
import { radioListType } from '../controls/xrx-radio.component';

@Component({
  selector: 'xrx-drm',
  templateUrl: './xrx-drm.component.html',
  styleUrls: ['./xrx-drm.component.less'],
  providers: [
    { provide: XrxControlBaseComponent, useExisting: XrxDrmComponent },
  ],
  standalone: false,
})
export class XrxDrmComponent extends XrxControlBaseComponent implements OnInit {
  @Input() required: boolean;
  @Input() label: string;
  @Input() value: string; // saved drm configuration
  @Input() isMfd: boolean;
  @ViewChild('f', { static: true }) public drmForm: NgForm;

  @Output() change = new EventEmitter<string>();
  @Output() validation = new EventEmitter<boolean>();

  isValid = true;
  drmFormIsInvalid = false;

  securityModes: radioListType[] = [
    {
      display: 'SDE_TRACK_VIEWS',
      value: SecurityMode.TrackViews,
      isChecked: false,
    },
    {
      display: 'SDE_TRACK_VIEWERS',
      value: SecurityMode.TrackViewers,
      isChecked: false,
    },
    {
      display: 'SDE_RESTRICT_TRACK_VIEWERS',
      value: SecurityMode.RestrictAndTrackViewers,
      isChecked: false,
    },
  ];

  expireModes: radioListType[] = [
    {
      display: 'SDE_CALENDAR_DATE',
      value: ExpireMode.Date,
      isChecked: false,
    },
    {
      display: 'SDE_NUMBER_OF_DAYS',
      value: ExpireMode.Days,
      isChecked: false,
    },
  ];

  maxReadsModes: radioListType[] = [
    {
      display: 'SDE_UNLIMITED',
      value: MaxReadsMode.Unlimited,
      isChecked: false,
    },
    {
      display: 'SDE_SPECIFIED',
      value: MaxReadsMode.Specified,
      isChecked: false,
    },
  ];

  drmConfig: DrmConfig = {
    securityMode: this.securityModes[0],
    maxReadsMode: this.maxReadsModes[0],
    maxReads: '',
    expireMode: this.expireModes[0],
    expireDate: '',
    expireDays: 60,
    enableUserList: false,
    emailDocument: false,
    senderAddress: '',
    documentOptions: {
      enabled: true,
      recipients: [],
      readerEmail: false,
      readTimes: true,
      readTiming: true,
      readNotification: false,
      proofOfSending: false,
      distRestriction: false,
      distributionList: false,
      domains: '',
      maxReads: 0,
      steganography: false,
      timestamp: false,
      print: false,
      verifyCode: false,
      expiryTime: '',
      language: '',
      allowResponsesTo: '',
      sideNote: '',
      subject: '',
      message: '',
    },
  };

  enableDomains = false;
  enterEmail = '';
  enterDomain = '';
  errorEmail = '';
  errorDomain = '';
  errorSubject = '';
  errorMaxReads = '';
  errorExpiration = '';
  respondEmail = '';
  maxUsers = '99';
  maxDomains = '10';
  maxExpireDays = 365;
  maxReadsPerReader = 999;
  expiryNgbDate: NgbDateStruct;
  expiryMinNgbDate: NgbDateStruct;
  expiryMaxNgbDate: NgbDateStruct;
  domains: string[] = []; // domain list shown in the UI (some entries could become invalid for saved workflows when domain restrictions are enabled)
  validDomains: string[] = []; // valid entries of domains list above
  allowedDomains: string[] = []; // domains allowed when workflow settings has domain restrictions enabled
  xdaTranslatePipe: XdaTranslate;
  maxDaysError: string;
  maxReadsError: string;
  maxDomainsError: string;
  maxEmailsError: string;
  languages: Language[];
  isEmailRestricted = false;
  isDomainsRestricted = false;
  isDomainRequiredError = false;
  isDomainError = false;

  constructor(
    private userService: UserService,
    private modalService: NgbModal,
    private xdaStringsService: XdaStringsService,
    private emailService: EmailService,
    private accessListService: AccessListService,
  ) {
    super();
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
    this.maxDaysError = this.xdaTranslatePipe.transform('SDE_VALUE_MUST_BE4', [
      '1',
      this.maxExpireDays.toString(),
    ]);
    this.maxReadsError = this.xdaTranslatePipe.transform('SDE_VALUE_MUST_BE4', [
      '1',
      this.maxReadsPerReader.toString(),
    ]);
    this.maxDomainsError = this.xdaTranslatePipe.transform(
      'SDE_MAXIMUM_FMTSTR_DOMAINS',
      [this.maxDomains],
    );
    this.maxEmailsError = this.xdaTranslatePipe.transform(
      'SDE_MAXIMUM_FMTSTR_EMAIL',
      [this.maxUsers],
    );
  }

  ngOnInit() {
    this.validation.emit(this.isValid);
    this.languages = Constants.languages;
    this.drmForm.valueChanges?.subscribe(() => this.statusChanges());
    this.loadSettings();
    this.loadDomains();
    this.validateEmailDomains();
  }

  statusChanges() {
    const invalid = this.drmForm.status === 'INVALID' ? true : false;
    if (this.drmFormIsInvalid !== invalid) {
      this.drmFormIsInvalid = invalid;
      this.validate();
    }
  }

  showHelp(e: Event) {
    this.modalService.open(SecurityLevelHelpComponent, {
      windowClass: 'xrx-modal modal-sm',
    });
  }

  selectSecurityMode() {
    if (
      this.drmConfig.securityMode.value !==
        SecurityMode.RestrictAndTrackViewers &&
      this.errorEmail == 'SDE_MIN1_EMAIL_ADDRESS'
    ) {
      // clear required email error
      setTimeout(() => {
        this.errorEmail = '';
      });
      this.clearError('enterEmail');
    }

    this.validateEmailDomains();

    if (
      this.drmConfig.securityMode.value === SecurityMode.RestrictAndTrackViewers
    ) {
      // add the document owner as a recipient to allow access to the RPD
      if (
        this.drmConfig.documentOptions.recipients.length < +this.maxUsers &&
        this.userService.user?.email &&
        this.isValidEmail(this.userService.user.email, false)
      ) {
        this.addRecipient(this.userService.user.email);
      }
    } else if (this.drmConfig.documentOptions.recipients.length) {
      this.drmConfig.enableUserList = true; // enable the User List toggle to show the recipients
    }

    // Parameter settings based on securityMode levels (API uses readerEmail and distRestriction to determine security level and other defaults)
    // Level 1: readerEmail = false, distRestriction = false
    // Level 2: readerEmail = true,  distRestriction = false
    // Level 3: readerEmail = true,  distRestriction = true
    this.drmConfig.documentOptions.readerEmail =
      this.drmConfig.securityMode.value != SecurityMode.TrackViews; // true for level 2 & 3
    this.drmConfig.documentOptions.distRestriction =
      this.drmConfig.securityMode.value == SecurityMode.RestrictAndTrackViewers; // true only for level 3

    // Turn on the email passcode checkbox
    this.drmConfig.documentOptions.verifyCode =
      this.drmConfig.securityMode.value != SecurityMode.TrackViews; // true for level 2 & 3

    this.updateDrmConfig();
  }

  onToggleSetting(setting: string) {
    if (setting === 'emailDocument' || setting === 'enableUserList') {
      this.drmConfig[setting] = !this.drmConfig[setting];
    } else {
      // Treat as a key of documentOptions and narrow the boolean type for safe indexing
      const key = setting as keyof typeof this.drmConfig.documentOptions;
      if (typeof this.drmConfig.documentOptions[key] === 'boolean') {
        (this.drmConfig.documentOptions[key] as boolean) = !(this.drmConfig
          .documentOptions[key] as boolean);
      }
    }
    this.updateDrmConfig();
  }

  onToggleDomains() {
    this.enableDomains = !this.enableDomains;
    this.validateEmailDomains();
  }

  onRespondEmailChange(e: Event) {
    e.stopPropagation();
    if (
      this.emailService.isValidEmailAddress(this.respondEmail) ||
      this.respondEmail.trim() === ''
    ) {
      this.drmConfig.documentOptions.allowResponsesTo =
        this.respondEmail.trim();
      this.updateDrmConfig();
    }
  }

  onSettingChange(e: Event | boolean) {
    if (typeof e !== 'boolean') {
      (e as Event).stopPropagation(); // prevent onChange from propagating to xrx-setting and changing value.
    }

    this.updateDrmConfig();
  }

  emailOnKeyup(e: Event) {
    this.enterEmail = this.emailService.cleanAddressList(this.enterEmail);
  }

  selectExpireMode() {
    if (this.drmConfig.expireMode.value === ExpireMode.Date) {
      this.onExpireDateChange();
    } else {
      this.onExpireDaysChange();
    }
  }

  onExpireDateChange() {
    // calendar picker date change
    this.drmConfig.documentOptions.expiryTime = new Date(
      this.expiryNgbDate.year,
      this.expiryNgbDate.month - 1,
      this.expiryNgbDate.day,
      23,
      59,
    ).toISOString();
    this.drmConfig.expireDate = this.drmConfig.documentOptions.expiryTime;

    if (
      new Date(this.drmConfig.expireDate) < new Date() &&
      !this.errorExpiration
    ) {
      setTimeout(() => {
        this.errorExpiration = 'SDE_EXPIRATION_DATE_PAST';
        this.setError('expiryDate');
      });
    } else if (this.errorExpiration) {
      setTimeout(() => {
        this.errorExpiration = '';
        this.clearError('expiryDate');
      });
    }

    this.updateDrmConfig();
  }

  onExpireDaysChange(e?: Event) {
    if (e) {
      e.stopPropagation();
    }

    // allow 1 to 365
    this.drmConfig.expireDays = Math.max(
      1,
      Math.min(Math.round(this.drmConfig.expireDays), this.maxExpireDays),
    );

    // expiration = number of days from now at 11:59pm
    const expires = new Date();
    expires.setDate(expires.getDate() + this.drmConfig.expireDays);
    expires.setHours(23, 59, 0, 0);
    this.drmConfig.documentOptions.expiryTime = expires.toISOString();

    this.updateDrmConfig();
  }

  selectMaxReadsMode() {
    if (this.drmConfig.maxReadsMode.value == MaxReadsMode.Unlimited) {
      this.drmConfig.documentOptions.maxReads = 0;
    }
    this.updateDrmConfig();
  }

  onMaxReadsChange(e: Event) {
    e.stopPropagation();

    // allow 1 to 999
    this.drmConfig.maxReads = Math.max(
      1,
      Math.min(Math.round(this.drmConfig.maxReads), this.maxReadsPerReader),
    );
    this.updateDrmConfig();
  }

  addRecipient(email: string) {
    // add if it doesn't exist
    if (
      !this.drmConfig.documentOptions.recipients.find(
        (x) => x.email.toLowerCase() == email.toLowerCase(),
      )
    ) {
      this.drmConfig.documentOptions.recipients.push({ email: email });
    }
  }

  addUserEmail(): boolean {
    const validEmails = this.validateEntries(this.enterEmail, this.isValidEmail);
    if (validEmails) {
      if (
        validEmails.length + this.drmConfig.documentOptions.recipients.length <=
        +this.maxUsers
      ) {
        validEmails.forEach((entry) => {
          this.addRecipient(entry);
        });

        this.enterEmail = ''; // clear the entry field
        this.updateDrmConfig();
        return true;
      } else {
        this.errorEmail = '';
        setTimeout(() => {
          this.errorEmail = this.maxEmailsError;
        });
        this.setError('enterEmail');
        return false;
      }
    } else {
      return false;
    }
  }

  removeUserEmail(index: number) {
    this.drmConfig.documentOptions.recipients.splice(index, 1);
    if (!this.drmConfig.documentOptions.recipients.length) {
      this.drmConfig.emailDocument = false; // turn off email toggle
    }
    this.validateEmailDomains();
    this.updateDrmConfig();

    if (this.errorEmail == this.maxEmailsError) {
      // clear the error
      setTimeout(() => {
        this.errorEmail = '';
      });
      this.clearError('enterEmail');
    }

    if (
      this.errorSubject != '' &&
      !this.drmConfig.documentOptions.recipients.length
    ) {
      // clear subject required error when user email list is empty.
      setTimeout(() => {
        this.errorSubject = '';
      });
      this.clearError('subject');
    }
  }

  editUserEmail(index: number) {
    const modalRef = this.modalService.open(EditAddressComponent, {
      windowClass: 'xrx-modal modal-md',
    });
    modalRef.componentInstance.type = AddressType.email;
    modalRef.componentInstance.address =
      this.drmConfig.documentOptions.recipients[index].email;
    modalRef.result.then((value) => {
      this.drmConfig.documentOptions.recipients[index].email = value;
      this.validateEmailDomains();
    });
  }

  addDomain(): boolean {
    const validDomains = this.validateEntries(
      this.enterDomain.toLowerCase(),
      this.isValidDomain,
    );
    if (validDomains) {
      if (validDomains.length + this.domains.length <= +this.maxDomains) {
        validDomains.forEach((entry) => {
          // add if it doesn't exist already
          if (
            !this.domains.find((x) => x.toLowerCase() == entry.toLowerCase())
          ) {
            this.domains.push(entry);
            this.validDomains.push(entry);
          }
        });

        this.enterDomain = ''; // clear the entry field
        this.drmConfig.documentOptions.domains = this.domains.join(',');
        this.validateEmailDomains();
        this.updateDrmConfig();
        return true;
      } else {
        this.errorDomain = '';
        setTimeout(() => {
          this.errorDomain = this.maxDomainsError;
        });
        this.drmForm.form.get('enterDomain')?.setErrors({ customerror: true });
        return false;
      }
    } else {
      return false;
    }
  }

  removeDomain(index: number) {
    if (this.isDomainsRestricted && this.domains.length == 1) {
      this.isDomainRequiredError = true; // show domain required error
      setTimeout(() => {
        this.isDomainRequiredError = false;
      }, 3000); // hide the error after 3s
      return;
    }

    this.domains.splice(index, 1);
    this.drmConfig.documentOptions.domains = this.domains.join(',');
    this.validateEmailDomains();
    this.validateDomains();
    this.updateDrmConfig();

    if (this.errorDomain == this.maxDomainsError) {
      // clear the error
      setTimeout(() => {
        this.errorDomain = '';
      });
      this.clearError('enterDomain');
    }
  }

  editDomain(index: number) {
    const modalRef = this.modalService.open(EditAddressComponent, {
      windowClass: 'xrx-modal modal-md',
    });
    modalRef.componentInstance.type = AddressType.domain;
    modalRef.componentInstance.address = this.domains[index];
    modalRef.result.then((value) => {
      this.domains[index] = value.toLower();
      this.drmConfig.documentOptions.domains = this.domains.join(',');
      this.validateEmailDomains();
    });
  }

  // Generic function call to validate a string containing one or more email or domain entries.
  //  entriesStr:  the string with one or more comma/semicolon separated values
  //  validateFunction: function to validate each entry which returns true/false
  validateEntries(
    entriesStr: string,
    validateFunction: (entry: string, showError: boolean) => boolean,
    showError = true,
  ) {
    entriesStr = entriesStr.trim();
    if (entriesStr.endsWith(';') || entriesStr.endsWith(',')) {
      entriesStr = entriesStr.slice(0, -1);
    }

    // remove any case-insensitive duplicates
    const entries: string[] = [];
    entriesStr.split(/[ ,;]+/).forEach(function (entry) {
      if (
        entry !== '' &&
        !(entries.includes(entry) || entries.includes(entry.toLowerCase()))
      )
        entries.push(entry.trim());
    });

    // check for any invalid entry
    const invalidIndex = entries.findIndex((entry) => {
      if (entry !== '') {
        return !validateFunction.bind(this)(entry, showError); // note: bind(this) makes 'this' accessible inside the validateFunction.
      }
      return;
    });

    // if all entries are valid return the array
    return invalidIndex == -1 ? entries : null;
  }

  isValidEmail(email: string, showError = true): boolean {
    // validate email format and if domain is allowed
    let error = '';
    if (!this.emailService.isValidEmailAddress(email)) {
      error = 'SDE_ONE_MORE_EMAIL3';
    } else if (!this.isAllowedDomain(email)) {
      error = 'SDE_ONE_MORE_EMAIL4';
    }

    if (error && showError) {
      this.errorEmail = '';
      setTimeout(() => {
        this.errorEmail = error;
      });
      this.drmForm.form.get('enterEmail')?.setErrors({ customerror: true });
    }

    return !error;
  }

  isValidDomain(domain: string, showError = true): boolean {
    // validate domain format
    this.errorDomain = '';
    if (domain.length && !this.emailService.isValidDomain(domain)) {
      if (showError) {
        setTimeout(() => {
          this.errorDomain = 'SDE_ONE_MORE_DOMAINS';
        });
        this.drmForm.form.get('enterDomain')?.setErrors({ customerror: true });
      }
      return false;
    }
    return true;
  }

  isAllowedDomain(email: string): boolean {
    // Is email domain in the domain list?
    if (this.enableDomains && this.domains.length) {
      const arr = email.split('@');
      const domain = arr[1];

      // For TrackViewers is it in the domains list?
      if (
        this.drmConfig.securityMode.value === SecurityMode.TrackViewers &&
        !this.domains.find((x) => x.toLowerCase() == domain.toLowerCase())
      ) {
        return false;
      }

      // Make sure it's allowed at the configuration level
      return this.accessListService.isAllowedDomain(domain) ? true : false;
    }
    return true;
  }

  validateEmailDomains() {
    // validate user email list against domains list and return error if any email in the list is restricted
    if (
      this.enableDomains &&
      this.domains.length &&
      this.drmConfig.documentOptions.recipients.length
    ) {
      const emails = Array.prototype.map
        .call(this.drmConfig.documentOptions.recipients, function (recipient) {
          return recipient.email;
        })
        .join(',');
      if (!this.validateEntries(emails, this.isValidEmail, false)) {
        this.isEmailRestricted = true;
        return;
      }
    }
    this.isEmailRestricted = false;
  }

  validateDomains() {
    // validate domains are in the allowed list and add to validDomains
    this.isDomainError = false;
    this.validDomains = [];
    this.domains.forEach((domain) => {
      if (this.accessListService.isAllowedDomain(domain)) {
        this.validDomains.push(domain);
      } else {
        this.errorDomain = this.xdaTranslatePipe.transform(
          'SDE_DOMAIN_FMTSTR_NOT1',
          [domain],
        );
        this.isDomainError = true;
      }
    });
  }

  // Called when navigate "Next" to Output settings
  validate() {
    this.isValid = !this.drmFormIsInvalid;

    if (this.drmConfig.enableUserList) {
      if (
        (this.enterEmail != '' && !this.addUserEmail()) ||
        this.isEmailRestricted
      ) {
        this.isValid = false;
      }
    }

    if (this.drmConfig.securityMode.value == SecurityMode.TrackViewers) {
      if (
        (this.enableDomains && this.enterDomain != '' && !this.addDomain()) ||
        this.isDomainError
      ) {
        this.isValid = false;
      }
    }

    if (
      this.drmConfig.securityMode.value ==
        SecurityMode.RestrictAndTrackViewers &&
      this.drmConfig.documentOptions.recipients.length == 0
    ) {
      this.isValid = false;
      this.errorEmail = '';
      setTimeout(() => {
        this.errorEmail = 'SDE_MIN1_EMAIL_ADDRESS';
      });
      this.setError('enterEmail');
    }

    if (
      this.drmConfig.documentOptions.recipients.length &&
      this.drmConfig.emailDocument &&
      !this.drmConfig.documentOptions.subject.trim().length
    ) {
      this.drmConfig.documentOptions.subject =
        this.drmConfig.documentOptions.subject.trim();
      this.isValid = false;
      setTimeout(() => {
        this.errorSubject = '';
        setTimeout(() => {
          this.errorSubject = 'SDE_REQUIRED_FIELD1';
        });
        this.setError('subject');
      });
    }

    if (this.drmConfig.documentOptions.recipients.length > +this.maxUsers) {
      this.isValid = false;
      this.errorEmail = '';
      setTimeout(() => {
        this.errorEmail = this.maxEmailsError;
      });
      this.setError('enterEmail');
    }

    if (
      this.drmConfig.maxReadsMode.value === MaxReadsMode.Specified &&
      this.drmConfig.maxReads === ''
    ) {
      this.isValid = false;
      this.errorMaxReads = '';
      setTimeout(() => {
        this.errorMaxReads = 'SDE_REQUIRED_FIELD1';
      });
      this.setError('maxreads');
    }

    if (this.drmConfig.expireMode.value === ExpireMode.Date) {
      const today = new Date(new Date().toDateString()); // compare just date parts
      const expireDate = new Date(
        new Date(this.drmConfig.expireDate).toDateString(),
      );
      if (expireDate <= today) {
        this.isValid = false;
        this.errorExpiration = '';
        setTimeout(() => {
          this.errorExpiration =
            expireDate < today
              ? 'SDE_EXPIRATION_DATE_PAST'
              : 'SDE_EXPIRATION_DATE_MUST';
        });
        this.setError('expiryDate');
      }
    }

    if (this.isValid) {
      // If User List is not enabled clear recipients and related settings
      if (
        !this.drmConfig.enableUserList &&
        this.drmConfig.securityMode.value !==
          SecurityMode.RestrictAndTrackViewers
      ) {
        this.drmConfig.documentOptions.recipients = [];
        this.drmConfig.emailDocument = false;
        this.drmConfig.documentOptions.distributionList = false;
      }

      // If MaxReadsMode is unlimited, set documentOptions.maxReads to 0 for unlimited, otherwise set from the UI bound value.
      if (this.drmConfig.maxReadsMode.value === MaxReadsMode.Unlimited) {
        this.drmConfig.documentOptions.maxReads = 0;
      } else {
        this.drmConfig.documentOptions.maxReads = this.drmConfig.maxReads;
      }

      this.updateDrmConfig();
    }

    this.validation.emit(this.isValid);
  }

  setError(control: string) {
    const ctrl = this.drmForm.form.get(control);
    if (ctrl) {
      ctrl.setErrors({ customerror: true });
      ctrl.markAsTouched();
      ctrl.markAsDirty();
    }
  }

  clearError(control: string) {
    const ctrl = this.drmForm.form.get(control);
    if (ctrl) {
      ctrl.setErrors({});
      ctrl.markAsPristine();
      ctrl.updateValueAndValidity();
    }
  }

  convertToNgbDate(date: string): NgbDateStruct {
    const dateObj = new Date(date);
    return {
      year: dateObj.getFullYear(),
      month: dateObj.getMonth() + 1,
      day: dateObj.getDate(),
    };
  }

  setExpirationRange() {
    // min expiration 1 day from now
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 1);
    minDate.setHours(23, 59, 0, 0);
    this.expiryMinNgbDate = this.convertToNgbDate(minDate.toISOString());

    // max expiration 1 year from now
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 1);
    maxDate.setHours(23, 59, 0, 0);
    this.expiryMaxNgbDate = this.convertToNgbDate(maxDate.toISOString());

    // set expire date for xrx-calendar control
    if (this.drmConfig.expireDate !== '') {
      this.expiryNgbDate = this.convertToNgbDate(this.drmConfig.expireDate);
    } else {
      // default is 60 days from now
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 60);
      expiryDate.setHours(23, 59, 0, 0);
      this.expiryNgbDate = this.convertToNgbDate(expiryDate.toISOString());
    }

    // set document expiryTime based on expireMode
    if (this.drmConfig.expireMode.value === ExpireMode.Date) {
      this.drmConfig.documentOptions.expiryTime = new Date(
        this.expiryNgbDate.year,
        this.expiryNgbDate.month - 1,
        this.expiryNgbDate.day,
        23,
        59,
      ).toISOString();
    } else {
      const expires = new Date();
      expires.setDate(expires.getDate() + this.drmConfig.expireDays);
      expires.setHours(23, 59, 0, 0);
      this.drmConfig.documentOptions.expiryTime = expires.toISOString();
    }
  }

  loadSettings() {
    if (this.value) {
      // saved settings in the case of saved workflow or returning from Input settings
      this.drmConfig = JSON.parse(this.value);
    }

    this.drmConfig.senderAddress = this.userService.user?.email || '';
    this.drmConfig.documentOptions.language =
      this.languages.find((x) => x.ISOCode === this.userService.user?.language)
        ?.regionCode || 'en-US';
    if (
      !this.value &&
      this.drmConfig.documentOptions.allowResponsesTo == '' &&
      this.userService.user?.email
    ) {
      this.drmConfig.documentOptions.allowResponsesTo =
        this.userService.user.email;
    }

    // other UI bindings
    this.domains = this.drmConfig.documentOptions.domains.length
      ? this.drmConfig.documentOptions.domains.split(',')
      : [];
    this.respondEmail = this.drmConfig.documentOptions.allowResponsesTo;
    this.enableDomains =
      this.drmConfig.securityMode.value == SecurityMode.TrackViewers &&
      this.domains.length > 0;
    this.enterEmail = '';
    this.enterDomain = '';
    this.setExpirationRange();
  }

  loadDomains() {
    if (
      this.userService.isAdvancedPlan &&
      this.accessListService.config?.enableDomainRestrictions
    ) {
      this.isDomainsRestricted = this.enableDomains = true;
      this.allowedDomains = this.accessListService.config?.allowedDomains;
      this.domains = this.drmConfig.documentOptions.domains
        ? this.drmConfig.documentOptions.domains.split(',')
        : this.accessListService.config?.allowedDomains.slice();
      this.drmConfig.documentOptions.domains = this.domains.join(',');
      this.validateDomains();
      this.updateDrmConfig();
    }
  }

  updateDrmConfig() {
    this.value = JSON.stringify(this.drmConfig);
    this.change.emit(this.value);
  }
}

enum SecurityMode {
  TrackViews = 1,
  TrackViewers = 2,
  RestrictAndTrackViewers = 3,
}

enum ExpireMode {
  Date = 0,
  Days = 1,
}

enum MaxReadsMode {
  Unlimited = 0,
  Specified = 1,
}

interface DrmConfig {
  securityMode: any;
  maxReadsMode: any;
  maxReads: any; // property to bind to UI due to 0 meaning unlimited
  expireMode: any; // keep track of using expireDate or expireDays for expiration
  expireDate: string;
  expireDays: number;
  enableUserList: boolean;
  emailDocument: boolean;
  senderAddress: string; // RPost API param
  documentOptions: {
    // RPost API params
    enabled: boolean;
    recipients: any[];
    readerEmail: boolean;
    readTimes: boolean;
    readTiming: boolean;
    readNotification: boolean;
    proofOfSending: boolean;
    distRestriction: boolean;
    distributionList: boolean;
    domains: string;
    maxReads: number;
    steganography: boolean;
    timestamp: boolean;
    print: boolean;
    verifyCode: boolean;
    expiryTime: string;
    language: string;
    allowResponsesTo: string;
    sideNote: string;
    subject: string;
    message: string;
  };
}
